import ExtendedPath from "csi/core/ExtendedPath";
import reflection from "csi/reflection";
import reflectionHints from "csi/reflectionHints";
import types from "csi/types";
import { C8Node, ICache, cnl } from "csi/typings";
import { LOCATION_RIGHT_PANE, LOCATION_RIGHT_PANE_URL, LOCATION_URL } from "csi/utility/routerUtility";
import xml from "csi/xml";
import { getRevisionNumber } from "./revision";

export const TYPE_COMMENT = "Comment";
export const TYPE_COMMENT_CATEGORY = "CommentCategory";
export const TYPE_COMMENT_REPLY = "CommentReply";
export const TYPE_PRODUCT_SOURCE = "ProductSource";

export const CONTAINER_COMMENTS = "Comments";
export const PATH_DOCUMENTS_COMMENTS = "Child:Documents/Child:Comments";
export const ATTR_NAMED_PARENT = "NamedParent";

export const ATTRIBUTES = {
	Author: "Author",
	Category: "Category",
	Comment: "Comment",
	DateAttr: "Date",
	Files: "Files",
	Images: reflection.ATTR_IMAGES,
	LastDate: "LastDate",
	Mentions: "Mentions",
	Revision: "Revision",
	Status: "Status",
	Subject: xml.ATTR_NAME,
	ThreeDInfo: "ThreeDInfo",
};

export enum PinColor {
	YELLOW = "#FCCD30",
	CYAN = "#38A9BA",
	BLUE = "#3C94F0",
	PINK = "#DE4780",
	PURPLE = "#7A65E6",
	RED = "#E75E4F"
}

export type PinData = {
	color: PinColor;
	commentUrl?: cnl;
	count?: string;
	position: PinPosition;
};

export interface PinPosition {
	x: number;
	y: number;
	z: number;
}

export function createStatusEnum(resolved: boolean): string {
	return xml.enumCreate("CommentStatus", resolved ? "Resolved" : "Open");
}

/** check for ThreeDInfo from node returning "null" as string . Ref: jira C8-27306*/
export function threeDInfoExists(threeDInfo: string): boolean {
	//To Do - this fix could be removed once this is handles in the backend to no send null as a string.
	return !!threeDInfo && (threeDInfo !== "null");
}

/** return CNL of comment's c8 instance (where comment was made) */
export function getCommentC8InstanceCnl(commentNode: C8Node, cache: ICache): cnl | undefined {
	if (!commentNode) {
		return;
	}

	let commentContainerNode = cache.get(commentNode.__Parent__);
	if (commentNode.$Type == TYPE_COMMENT_REPLY) {
		commentContainerNode = cache.get(commentContainerNode.__Parent__);
	}

	return commentContainerNode?.NamedParent;
}

/** return revision count within master for referenced comment revision */
export function getCommentRevisionCount(commentNode: C8Node, cache: ICache): number {
	const { Revision } = commentNode;
	const revisionNode = cache.get(Revision);
	const masterNode = cache.get(revisionNode?.Master);
	return masterNode ? getRevisionNumber(Revision, masterNode.Revisions, revisionNode?.$Name) : 0;
}

/** get the related type based on the primary type  */
export function getRelatedTypes(type: string): string[] {
	const paths = reflectionHints.getCommentPaths(type);
	const rc = reflection.getClass(type);

	return paths.map((path) => {
		const rcDest = ExtendedPath.getPathEndClass(rc, path);

		return rcDest.getType();
	});
}

/**
 * Return extra paths to related type or comments to use in Comments view of given type
 * @param {string} type
 * @return {string[]}
 */
export function getResolvedCommentPaths(type: string): string[] {
	const paths = reflectionHints.getCommentPaths(type);
	const rc = reflection.getClass(type);
	const dcoSuffix = xml.path("DocumentsAndComments", "Documents", "Comments");
	const dcSuffix = xml.path("DocumentsAndComments", "Comments");
	const ccSuffix = xml.path("Comments");
	const fullPaths = [];
	paths.forEach((path) => {
		const rcDest = ExtendedPath.getPathEndClass(rc, path);

		if (rcDest.isDerivedFrom("CommentContainer")) {
			fullPaths.push(xml.path(path, ccSuffix));
		}

		if (rcDest.isDerivedFrom("DocAndCommentOwner")) {
			fullPaths.push(xml.path(path, dcSuffix));
			fullPaths.push(xml.path(path, dcoSuffix));
		}
	});

	return fullPaths;
}

export function getDate(comment: C8Node): string {
	return types.getFormattedDateDifference(types.getTimeDate(comment.Date), true);
}

export function getStatusDate(comment: C8Node): string {
	return types.getFormattedDateDifference(types.getTimeDate(comment.StatusTime), true);
}

/** return location parameter for rightpane comment view, or null if the comment doesn't exist */
export function getCommentLocationParameter(commentCnl: cnl, cache: ICache): object | null {
	const commentContainerNode = cache.get(commentCnl);
	if (!commentContainerNode) {
		return null;
	}

	const url = getCommentC8InstanceCnl(commentContainerNode, cache);
	return {
		[LOCATION_URL]: url,
		[LOCATION_RIGHT_PANE]: "Conversations",
		[LOCATION_RIGHT_PANE_URL]: commentContainerNode.$Type === TYPE_COMMENT_REPLY ? commentContainerNode.__Parent__ : commentCnl,
	};
}
